// bluaIQ — AI-DRAFT-TAKEOFF-1: static demo fixture for the AI Draft Takeoff shell.
//
// DEMO DATA ONLY. This is a hand-written, synthetic `AiDraftTakeoffReviewQueueItem`
// typed against the INTEGRATION-MAP-1 canonical contracts. It is NOT produced by any
// AI system, OCR, PDF parser, or measurement engine — it is a fixed literal used to
// render the read-only demo shell. It creates nothing, writes nothing, and reaches
// no backend. The projectId is a synthetic demo id, never a real production project.

import type {
  AiDraftTakeoffReviewQueueItem,
  AiDraftTakeoffQuantityRow,
  HandoffApprovalState,
  HandoffAssumption,
  HandoffBlocker,
  HandoffBoundary,
  HandoffForbiddenCategory,
} from '../../shared/integrationHandoffContracts'

/** Synthetic demo project id — deliberately NOT a real cuid / production id. */
export const AI_DRAFT_TAKEOFF_DEMO_PROJECT_ID = 'demo-ai-draft-takeoff-0001'

/**
 * A single preliminary AI Draft Takeoff review-queue item, fully shaped to the
 * INTEGRATION-MAP-1 envelope: AI_DRAFT approval state, READ_ONLY boundary, human
 * approval required, write boundary disallowing writes, provenance + confidence +
 * assumptions + blockers + forbidden categories + no-silent-drop all present.
 */
export const aiDraftTakeoffDemoFixture: AiDraftTakeoffReviewQueueItem = {
  schemaVersion: 'integration-map-1',
  handoffId: 'demo-aidt-handoff-0001',
  sourceModule: 'AI_DRAFT_TAKEOFF',
  targetModule: 'TAKEOFF',
  projectId: AI_DRAFT_TAKEOFF_DEMO_PROJECT_ID,
  createdAt: '2026-07-05T00:00:00.000Z',
  approvalState: 'AI_DRAFT',
  boundary: 'READ_ONLY',
  payload: {
    label: { en: 'AI Draft Takeoff', es: 'Cuantificación preliminar IA' },
    preliminary: true,
    canDriveQuantity: false,
    stageGroups: [
      {
        stageKey: 'foundation',
        stageLabel: 'Foundation',
        quantityRows: [
          {
            upstreamId: 'aidt-row-foundation-concrete',
            description: 'Slab-on-grade concrete (demo estimate)',
            quantity: 42,
            unit: 'cy',
            confidence: { value: 0.62, basis: 'Demo estimate — plan scale unconfirmed', requiresHumanConfirmation: true },
            assumptions: [
              {
                id: 'aidt-asm-slab-thickness',
                statement: 'Assumed 4in slab thickness (demo default)',
                impactIfWrong: 'Concrete volume changes proportionally',
                mustConfirm: true,
              },
            ],
          },
          {
            upstreamId: 'aidt-row-foundation-rebar',
            description: 'Rebar #4 (demo estimate)',
            quantity: 1850,
            unit: 'lf',
            confidence: { value: 0.55, basis: 'Demo estimate — reinforcement spec ambiguous', requiresHumanConfirmation: true },
          },
        ],
      },
      {
        stageKey: 'framing',
        stageLabel: 'Framing',
        quantityRows: [
          {
            upstreamId: 'aidt-row-framing-studs',
            description: '2x4 studs, exterior walls (demo estimate)',
            quantity: 420,
            unit: 'ea',
            confidence: { value: 0.71, basis: 'Demo estimate — wall lengths approximate', requiresHumanConfirmation: true },
          },
          {
            upstreamId: 'aidt-row-framing-sheathing',
            description: 'Wall sheathing (demo estimate)',
            quantity: 96,
            unit: 'sheet',
            confidence: { value: 0.68, basis: 'Demo estimate — opening deductions not applied', requiresHumanConfirmation: true },
          },
        ],
      },
    ],
    assumptions: [
      {
        id: 'aidt-asm-plan-scale',
        statement: 'Plan scale assumed 1/4in = 1ft (demo default — unverified)',
        impactIfWrong: 'All linear/area quantities scale incorrectly',
        mustConfirm: true,
      },
    ],
    blockers: [
      {
        code: 'demo_scale_unconfirmed',
        message: 'Plan scale is unconfirmed — quantities are preliminary only',
        severity: 'BLOCKING',
        isBlocking: true,
      },
      {
        code: 'demo_openings_not_deducted',
        message: 'Window/door openings not yet deducted from wall areas',
        severity: 'WARNING',
        isBlocking: false,
      },
    ],
    forbiddenCategories: [
      {
        code: 'structural_engineering',
        label: 'Structural engineering quantities',
        reason: 'Demo shell must never propose engineered structural quantities',
        mustCarryForward: true,
      },
    ],
    noSilentDrop: {
      totalCandidates: 6,
      retained: 4,
      dropped: [
        { upstreamId: 'aidt-cand-illegible-note', reason: 'Illegible handwritten note — excluded from demo', category: 'unreadable' },
        { upstreamId: 'aidt-cand-out-of-scope-legend', reason: 'Legend entry out of demo scope', category: 'out_of_scope' },
      ],
    },
  },
  provenance: {
    sourceModule: 'AI_DRAFT_TAKEOFF',
    upstreamId: 'aidt-demo-source-0001',
    originKind: 'AI_DRAFT',
    sourceDocumentRefs: ['demo-plan-sheet-A1', 'demo-plan-sheet-A2'],
    derivationVersion: 'demo-fixture-1',
    createdAt: '2026-07-05T00:00:00.000Z',
  },
  confidence: { value: 0.6, basis: 'Overall demo confidence — preliminary, needs human review', requiresHumanConfirmation: true },
  assumptions: [
    {
      id: 'aidt-asm-waste',
      statement: 'No waste factor applied in the demo fixture',
      impactIfWrong: 'Real quantities would be higher once waste is added',
      mustConfirm: true,
    },
  ],
  blockers: [
    {
      code: 'demo_human_review_required',
      message: 'Preliminary demo — human review required before any use',
      severity: 'BLOCKING',
      isBlocking: true,
    },
  ],
  forbiddenCategories: [
    {
      code: 'structural_engineering',
      label: 'Structural engineering quantities',
      reason: 'Carried forward from the payload — never proposes engineered structural quantities',
      mustCarryForward: true,
    },
  ],
  noSilentDrop: {
    totalCandidates: 6,
    retained: 4,
    dropped: [
      { upstreamId: 'aidt-cand-illegible-note', reason: 'Illegible handwritten note — excluded from demo', category: 'unreadable' },
      { upstreamId: 'aidt-cand-out-of-scope-legend', reason: 'Legend entry out of demo scope', category: 'out_of_scope' },
    ],
  },
  versioning: {
    schemaVersion: 'integration-map-1',
    revision: 1,
    supersedesHandoffId: null,
    parserVersion: 'demo-fixture-1',
  },
  idempotency: { idempotencyKey: 'demo-aidt-idem-0001', contentHash: null },
  humanApproval: { required: true, state: 'AI_DRAFT', approvedBy: null, approvedAt: null, note: null },
  writeBoundary: { boundary: 'READ_ONLY', allowsWrite: false, targetWritesRequireApproval: true },
}

// ── AI-DRAFT-TAKEOFF-2: read-only review-queue demo (multiple simulated items) ─────
//
// A small array of synthetic `AiDraftTakeoffReviewQueueItem`s used ONLY to render the
// read-only review-queue demo. Every item is a fixed literal — none is produced by any
// AI system, OCR, PDF parser, or measurement engine. Every item keeps the same hard
// safety posture as the single fixture above: humanApproval.required = true,
// writeBoundary.allowsWrite = false, forbidden categories carry forward, and every
// dropped candidate records an upstreamId + reason (no silent drop). Nothing here
// writes anything or reaches any backend.

/** Synthetic demo project id for the queue — deliberately NOT a real production id. */
export const AI_DRAFT_TAKEOFF_DEMO_QUEUE_PROJECT_ID = 'demo-ai-draft-takeoff-queue-0001'

/** Forbidden category carried forward by every queue item (audit-visible). */
const FORBIDDEN_STRUCTURAL: HandoffForbiddenCategory = {
  code: 'structural_engineering',
  label: 'Structural engineering quantities',
  reason: 'Demo queue must never propose engineered structural quantities',
  mustCarryForward: true,
}

/** Build one fully-shaped, safe queue item from a compact seed (keeps every required
 *  safety field present and consistent — no field can be silently omitted). */
function makeQueueItem(seed: {
  n: number
  handoffId: string
  approvalState: HandoffApprovalState
  boundary: HandoffBoundary
  stageKey: string
  stageLabel: string
  rows: AiDraftTakeoffQuantityRow[]
  overallConfidence: number
  confidenceBasis: string
  assumptions: HandoffAssumption[]
  blockers: HandoffBlocker[]
  droppedReason: string
  supersedesHandoffId?: string | null
  revision?: number
}): AiDraftTakeoffReviewQueueItem {
  const noSilentDrop = {
    totalCandidates: seed.rows.length + 1,
    retained: seed.rows.length,
    dropped: [
      { upstreamId: `${seed.handoffId}-drop-1`, reason: seed.droppedReason, category: 'out_of_scope' },
    ],
  }
  return {
    schemaVersion: 'integration-map-1',
    handoffId: seed.handoffId,
    sourceModule: 'AI_DRAFT_TAKEOFF',
    targetModule: 'TAKEOFF',
    projectId: `demo-ai-draft-takeoff-queue-000${seed.n}`,
    createdAt: '2026-07-05T00:00:00.000Z',
    approvalState: seed.approvalState,
    boundary: seed.boundary,
    payload: {
      label: { en: 'AI Draft Takeoff', es: 'Cuantificación preliminar IA' },
      preliminary: true,
      canDriveQuantity: false,
      stageGroups: [{ stageKey: seed.stageKey, stageLabel: seed.stageLabel, quantityRows: seed.rows }],
      assumptions: seed.assumptions,
      blockers: seed.blockers,
      forbiddenCategories: [FORBIDDEN_STRUCTURAL],
      noSilentDrop,
    },
    provenance: {
      sourceModule: 'AI_DRAFT_TAKEOFF',
      upstreamId: `${seed.handoffId}-source`,
      originKind: 'AI_DRAFT',
      sourceDocumentRefs: ['demo-plan-sheet-A1', 'demo-plan-sheet-A2'],
      derivationVersion: 'demo-fixture-2',
      createdAt: '2026-07-05T00:00:00.000Z',
    },
    confidence: { value: seed.overallConfidence, basis: seed.confidenceBasis, requiresHumanConfirmation: true },
    assumptions: seed.assumptions,
    blockers: seed.blockers,
    forbiddenCategories: [FORBIDDEN_STRUCTURAL],
    noSilentDrop,
    versioning: {
      schemaVersion: 'integration-map-1',
      revision: seed.revision ?? 1,
      supersedesHandoffId: seed.supersedesHandoffId ?? null,
      parserVersion: 'demo-fixture-2',
    },
    idempotency: { idempotencyKey: `${seed.handoffId}-idem`, contentHash: null },
    humanApproval: { required: true, state: seed.approvalState, approvedBy: null, approvedAt: null, note: null },
    writeBoundary: { boundary: seed.boundary, allowsWrite: false, targetWritesRequireApproval: true },
  }
}

/**
 * The read-only review queue: 4 simulated items across the AI_DRAFT,
 * HUMAN_REVIEW_REQUIRED, BLOCKED and (optional) SUPERSEDED lifecycle states.
 */
export const aiDraftTakeoffDemoQueueItems: AiDraftTakeoffReviewQueueItem[] = [
  makeQueueItem({
    n: 1,
    handoffId: 'demo-aidt-queue-0001',
    approvalState: 'AI_DRAFT',
    boundary: 'READ_ONLY',
    stageKey: 'foundation',
    stageLabel: 'Foundation',
    rows: [
      {
        upstreamId: 'q1-row-concrete',
        description: 'Slab-on-grade concrete (demo estimate)',
        quantity: 42,
        unit: 'cy',
        confidence: { value: 0.62, basis: 'Demo estimate — plan scale unconfirmed', requiresHumanConfirmation: true },
      },
    ],
    overallConfidence: 0.62,
    confidenceBasis: 'Preliminary demo — needs human review before any use',
    assumptions: [
      { id: 'q1-asm-slab', statement: 'Assumed 4in slab thickness (demo default)', impactIfWrong: 'Concrete volume changes proportionally', mustConfirm: true },
    ],
    blockers: [
      { code: 'demo_openings_not_deducted', message: 'Window/door openings not yet deducted', severity: 'WARNING', isBlocking: false },
    ],
    droppedReason: 'Illegible handwritten note — excluded from demo',
  }),
  makeQueueItem({
    n: 2,
    handoffId: 'demo-aidt-queue-0002',
    approvalState: 'HUMAN_REVIEW_REQUIRED',
    boundary: 'PROPOSAL_ONLY',
    stageKey: 'roofing',
    stageLabel: 'Roofing & envelope',
    rows: [
      {
        upstreamId: 'q2-row-shingles',
        description: 'Architectural shingles (demo estimate)',
        quantity: 38,
        unit: 'sq',
        confidence: { value: 0.58, basis: 'Demo estimate — roof pitch ambiguous', requiresHumanConfirmation: true },
      },
    ],
    overallConfidence: 0.58,
    confidenceBasis: 'Demo estimate — requires human review of pitch/waste',
    assumptions: [
      { id: 'q2-asm-waste', statement: 'Assumed 12% roofing waste (demo default)', impactIfWrong: 'Material total changes', mustConfirm: true },
    ],
    blockers: [
      { code: 'demo_pitch_ambiguous', message: 'Roof pitch not confirmed — human review required', severity: 'WARNING', isBlocking: false },
    ],
    droppedReason: 'Detail callout out of demo scope',
  }),
  makeQueueItem({
    n: 3,
    handoffId: 'demo-aidt-queue-0003',
    approvalState: 'BLOCKED',
    boundary: 'READ_ONLY',
    stageKey: 'electrical',
    stageLabel: 'Electrical rough-in',
    rows: [
      {
        upstreamId: 'q3-row-devices',
        description: 'Device rough-in count (demo estimate)',
        quantity: 96,
        unit: 'ea',
        confidence: { value: 0.4, basis: 'Demo estimate — legend unreadable', requiresHumanConfirmation: true },
      },
    ],
    overallConfidence: 0.4,
    confidenceBasis: 'Blocked — plan scale unconfirmed, cannot proceed',
    assumptions: [
      { id: 'q3-asm-scale', statement: 'Plan scale assumed 1/4in = 1ft (unverified)', impactIfWrong: 'All quantities scale incorrectly', mustConfirm: true },
    ],
    blockers: [
      { code: 'demo_scale_unconfirmed', message: 'Plan scale unconfirmed — progression halted', severity: 'BLOCKING', isBlocking: true },
    ],
    droppedReason: 'Symbol not in demo legend',
  }),
  makeQueueItem({
    n: 4,
    handoffId: 'demo-aidt-queue-0004',
    approvalState: 'SUPERSEDED',
    boundary: 'READ_ONLY',
    stageKey: 'sitework',
    stageLabel: 'Sitework (v1 — superseded)',
    rows: [
      {
        upstreamId: 'q4-row-grading',
        description: 'Rough grading area (demo estimate)',
        quantity: 5200,
        unit: 'sf',
        confidence: { value: 0.5, basis: 'Demo estimate — superseded by a newer draft', requiresHumanConfirmation: true },
      },
    ],
    overallConfidence: 0.5,
    confidenceBasis: 'Superseded — retained for audit trail only',
    assumptions: [
      { id: 'q4-asm-boundary', statement: 'Assumed lot boundary from demo sheet (superseded)', impactIfWrong: 'Area changes', mustConfirm: true },
    ],
    blockers: [
      { code: 'demo_superseded', message: 'Superseded by a newer demo draft', severity: 'INFO', isBlocking: false },
    ],
    droppedReason: 'Superseded line removed in the newer draft',
    supersedesHandoffId: 'demo-aidt-queue-0004-v0',
    revision: 2,
  }),
]
